import { makeAutoObservable } from 'mobx'
import { getRoleMenuActions } from '../api/authApi'
import { getOrgContext } from '../api/contextApi'
import { encodePermissions, decodePermissions } from '../../util/obfuscation'
import keycloak from '../../config/keycloak'
import axiosInstance from '../api/axiosInstance'
import type { MenuActionItem, PermissionLookup } from '../../types'

// ── Keycloak token payload shape (custom claims added by Keycloak mapper) ─────
interface KeycloakTokenParsed {
  sub?: string             // Keycloak UUID (not the CRM DB user ID)
  email?: string
  preferred_username?: string
  name?: string
  given_name?: string
  mobile?: string
  roleId?: string          // integer string, e.g. "35"
  roleName?: string        // role name claim, e.g. "SUPER_ADMIN"
  dbId?: string            // CRM DB user identifier
  orgId?: string           // org GUID claim added by Keycloak mapper
  lobId?: string           // lob GUID claim added by Keycloak mapper
  org_id?: string          // alternate claim name
  lob_id?: string          // alternate claim name
  warehouseId?: string
  warehouse_id?: string
  exp?: number
  iat?: number
}

const USER_PROFILE_KEY = 'crm_user_profile'

interface AuthUser {
  userId: string           // dbId from token (CRM identity reference)
  email: string
  name: string | null
  phone: string | null
  mobile: string | null    // from Keycloak token 'mobile' claim
  roleName: string
  intRoleId: number | null        // integer roleId from Keycloak token
  permissions: string[]           // legacy flat list (kept for backward compat)
  // ── Granular permissions ──────────────────────────────────────────────────
  permissionsMap: PermissionLookup // flattened O(1) path → action → boolean map
  // ── Multi-tenancy ─────────────────────────────────────────────────────────
  orgId: string
  lobId: string
  warehouseId: string
  // ── Human-readable tenant names (populated by loadOrgContext) ─────────────
  orgName: string | null
  lobName: string | null
  // ── Profile (populated by loadProfile) ────────────────────────────────────
  agentId: number | null
  fcmToken: string | null
}

const MENU_PERMISSIONS_KEY = 'crm_menu_permissions'
const PERMISSIONS_KEY = 'crm_permissions'

class AuthStore {
  user: AuthUser | null = null
  isLoading = false
  error: string | null = null

  constructor() {
    makeAutoObservable(this)
  }

  get isAuthenticated(): boolean {
    return !!this.user
  }

  get isSuperAdmin(): boolean {
    return !this.user?.orgId
  }

  get isOrgAdmin(): boolean {
    return !!this.user?.orgId && !this.user?.lobId
  }

  get isLobAdmin(): boolean {
    if (!this.user?.permissionsMap) return false
    const crmActions = this.user.permissionsMap['/crm']
    return Boolean(crmActions?.['islobadmin'] || crmActions?.['is_lob_admin'])
  }

  get canViewLobWideData(): boolean {
    return this.isSuperAdmin || this.isOrgAdmin || this.isLobAdmin
  }

  /** True once the User Manager has returned and permissionsMap is populated. */
  get hasPermissionsLoaded(): boolean {
    return Object.keys(this.user?.permissionsMap ?? {}).length > 0
  }

  /** Returns the current Keycloak access token (refreshed automatically by axiosInstance). */
  get accessToken(): string | null {
    return keycloak.token ?? null
  }

  get userInitials(): string {
    if (!this.user?.email) return '?'
    const parts = this.user.email.split('@')[0].split(/[._-]/)
    return parts
      .slice(0, 2)
      .map((p) => p[0]?.toUpperCase() ?? '')
      .join('')
  }

  // ── Initialise from Keycloak token ─────────────────────────────────────────
  // Called once after Keycloak authenticates (fresh login or page refresh).
  // Reads all user context directly from keycloak.tokenParsed — no API call needed.

  async initFromKeycloakToken(): Promise<void> {
    if (!keycloak.token || !keycloak.tokenParsed) {
      throw new Error('No Keycloak token available')
    }

    // Refresh token if it's about to expire (< 30s left)
    try {
      await keycloak.updateToken(30)
    } catch {
      keycloak.login()
      return
    }

    const parsed = keycloak.tokenParsed as KeycloakTokenParsed

    // Restore cached profile from localStorage
    const cachedProfile = this._loadCachedProfile()

    this.user = {
      userId:      parsed.dbId ?? parsed.sub ?? '',
      email:       parsed.email ?? parsed.preferred_username ?? '',
      name:        parsed.name ?? parsed.given_name ?? null,
      phone:       cachedProfile?.phone ?? null,
      mobile:      parsed.mobile ?? null,
      roleName:    parsed.roleName ?? '',
      intRoleId:   parsed.roleId ? parseInt(parsed.roleId, 10) : null,
      permissions: decodePermissions<string[]>(localStorage.getItem(PERMISSIONS_KEY), []),
      permissionsMap: {},
      orgId:       parsed.orgId ?? parsed.org_id ?? '',
      lobId:       parsed.lobId ?? parsed.lob_id ?? '',
      warehouseId: parsed.warehouseId ?? parsed.warehouse_id ?? '',
      orgName:     cachedProfile?.orgName ?? null,
      lobName:     cachedProfile?.lobName ?? null,
      agentId:     cachedProfile?.agentId ?? null,
      fcmToken:    cachedProfile?.fcmToken ?? null,
    }

    // Restore cached granular permissions (survives page refresh)
    const cachedMenuItems = decodePermissions<MenuActionItem[]>(
      localStorage.getItem(MENU_PERMISSIONS_KEY),
      [],
    )
    if (cachedMenuItems.length > 0) {
      this.user.permissionsMap = this.flattenPermissions(cachedMenuItems)
    }

    await this.fetchRoleMenuPermissions()
  }

  clearSession() {
    this.user = null
    localStorage.removeItem(MENU_PERMISSIONS_KEY)
    localStorage.removeItem(PERMISSIONS_KEY)
    localStorage.removeItem(USER_PROFILE_KEY)
  }

  // ── Granular role-menu-action permissions ──────────────────────────────────

  async fetchRoleMenuPermissions(): Promise<void> {
    const roleId = this.user?.intRoleId
    if (!roleId) {
      return
    }
    try {
      const menuItems = await getRoleMenuActions(roleId)

      if (!menuItems?.length) {
        return
      }

      const map = this.flattenPermissions(menuItems)

      if (this.user) this.user.permissionsMap = map
      localStorage.setItem(MENU_PERMISSIONS_KEY, encodePermissions(menuItems))
    } catch (err: any) {
      this.error = 'Failed to fetch permissions.'
    }
  }

  private flattenPermissions(items: MenuActionItem[]): PermissionLookup {
    const map: PermissionLookup = {}

    const traverse = (item: MenuActionItem | import('../../types').MenuChild) => {
      if (item.url) {
        // Normalise the raw URL: strip leading/trailing slashes, lower-case
        const clean    = item.url.replace(/^\/|\/$/g, '').toLowerCase()
        const segments = clean.split('/')

        // ── First-segment key (legacy behaviour, e.g. "/sales") ──────────────
        const firstKey = '/' + segments[0]
        map[firstKey]  = map[firstKey] ?? {}

        // ── Full-path key for multi-segment URLs (e.g. "/settings/email-files") ──
        // Only needed when there are two or more segments so that sub-pages can
        // be ACL-gated individually without colliding with the parent key.
        const fullKey = '/' + clean
        if (segments.length > 1) {
          map[fullKey] = map[fullKey] ?? {}
        }

        item.actions?.forEach((act) => {
          const actionKey = act.actionName.toLowerCase()
          map[firstKey][actionKey] = act.isChecked
          if (segments.length > 1) {
            map[fullKey][actionKey] = act.isChecked
          }
        })
      }

      if ('children' in item && item.children) {
        item.children.forEach(traverse)
      }
    }

    items.forEach(traverse)
    return map
  }


  // ── Logout ─────────────────────────────────────────────────────────────────

  async logout(): Promise<void> {
    this.clearSession()
    keycloak.logout({ redirectUri: window.location.origin })
  }

  async logoutAll(): Promise<void> {
    this.clearSession()
    keycloak.logout({ redirectUri: window.location.origin })
  }

  // ── Profile ────────────────────────────────────────────────────────────────

  /**
   * Fetch the full user profile via GET /users/me.
   * Populates phone, agentId, fcmToken and saves them to localStorage for offline use.
   */
  async loadProfile(): Promise<void> {
    if (!this.user) return
    try {
      const res = await axiosInstance.get<{
        data: {
          id: string
          name: string
          email: string
          phone: string | null
          orgId: string | null
          lobId: string | null
          warehouseId: string | null
          agentId: number | null
          fcmToken: string | null
        }
      }>('/users/me')

      const profile = res.data.data
      if (this.user) {
        this.user.name     = profile.name
        this.user.phone    = profile.phone
        this.user.agentId  = profile.agentId
        this.user.fcmToken = profile.fcmToken

        // Persist profile fields to localStorage for next page load
        this._saveCachedProfile({
          phone:    profile.phone,
          agentId:  profile.agentId,
          fcmToken: profile.fcmToken,
          orgName:  this.user.orgName,
          lobName:  this.user.lobName,
        })
      }
    } catch {
      // Non-fatal — retry once after 3s
      setTimeout(() => {
        if (this.user && this.user.agentId === null) {
          axiosInstance
            .get<{ data: { phone: string | null; agentId: number | null; fcmToken: string | null } }>('/users/me')
            .then((res) => {
              if (this.user) {
                this.user.phone    = res.data.data.phone
                this.user.agentId  = res.data.data.agentId
                this.user.fcmToken = res.data.data.fcmToken
                this._saveCachedProfile({
                  phone:    res.data.data.phone,
                  agentId:  res.data.data.agentId,
                  fcmToken: res.data.data.fcmToken,
                  orgName:  this.user.orgName,
                  lobName:  this.user.lobName,
                })
              }
            })
            .catch(() => { /* give up silently */ })
        }
      }, 3000)
    }
  }

  /**
   * Fetch org/LOB names via GET /context/orgs.
   * Populates orgName and lobName on the user object and caches them.
   * Called after loadProfile so the user object already exists.
   */
  async loadOrgContext(): Promise<void> {
    if (!this.user) return
    try {
      const orgs = await getOrgContext()
      if (!this.user || orgs.length === 0) return

      // For non-SuperAdmin there will be exactly one org
      const org = orgs[0]
      this.user.orgName = org.name

      // lobName: find the LOB matching the user's lobId (if assigned)
      const lob = org.lobs.find(l => l.id === this.user!.lobId)
      this.user.lobName = lob?.name ?? (org.lobs.length === 1 ? org.lobs[0].name : null)

      // Persist so it's available on next page refresh before the API call completes
      this._saveCachedProfile({
        phone:    this.user.phone,
        agentId:  this.user.agentId,
        fcmToken: this.user.fcmToken,
        orgName:  this.user.orgName,
        lobName:  this.user.lobName,
      })
    } catch (err) {
      // ignore
    }
  }

  clearError() {
    this.error = null
  }

  // ── localStorage helpers ───────────────────────────────────────────────────

  private _saveCachedProfile(data: {
    phone: string | null
    agentId: number | null
    fcmToken: string | null
    orgName?: string | null
    lobName?: string | null
  }) {
    try {
      localStorage.setItem(USER_PROFILE_KEY, JSON.stringify(data))
    } catch { /* ignore quota errors */ }
  }

  private _loadCachedProfile(): {
    phone: string | null
    agentId: number | null
    fcmToken: string | null
    orgName?: string | null
    lobName?: string | null
  } | null {
    try {
      const raw = localStorage.getItem(USER_PROFILE_KEY)
      return raw ? JSON.parse(raw) : null
    } catch {
      return null
    }
  }
}

export const authStore = new AuthStore()
